import {
  ErrorCode,
  type ApiErrorBody,
  type Envelope,
  type ErrorEnvelope,
} from './types';

/**
 * Cliente HTTP da SeguraCore.
 *
 * Responsabilidades:
 *   - anexar o token de acesso corrente;
 *   - renovar a sessao uma unica vez quando o token expira, mesmo que varias
 *     requisicoes falhem ao mesmo tempo;
 *   - transformar o envelope de erro da API em um erro tipado.
 *
 * O token de acesso vive apenas em memoria. Nao vai para localStorage, que e
 * legivel por qualquer script carregado na pagina. Ver docs/AUTHENTICATION.md.
 */

const baseUrl = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: Record<string, unknown> | undefined;
  readonly requestId: string | undefined;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.details = body.details;
    this.requestId = body.request_id;
  }

  /** Indica que a sessao precisa ser refeita pelo usuario. */
  get isAuthFailure(): boolean {
    return (
      this.code === ErrorCode.Unauthenticated ||
      this.code === ErrorCode.SessionExpired ||
      this.code === ErrorCode.SessionRevoked ||
      this.code === ErrorCode.RefreshTokenReused
    );
  }
}

interface TokenState {
  accessToken: string | null;
  refreshToken: string | null;
}

const tokens: TokenState = { accessToken: null, refreshToken: null };

let onSessionLost: (() => void) | null = null;
let refreshInFlight: Promise<boolean> | null = null;

export function setTokens(accessToken: string, refreshToken: string): void {
  tokens.accessToken = accessToken;
  tokens.refreshToken = refreshToken;
}

export function clearTokens(): void {
  tokens.accessToken = null;
  tokens.refreshToken = null;
}

export function hasSession(): boolean {
  return tokens.accessToken !== null;
}

/** Registra o que fazer quando a sessao nao pode mais ser renovada. */
export function setSessionLostHandler(handler: () => void): void {
  onSessionLost = handler;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** Rotas publicas nao tentam renovar a sessao ao receber 401. */
  skipAuth?: boolean;
}

export async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  return handle<T>(await execute(path, options));
}

/**
 * Executa a requisicao e trata a sessao expirada.
 *
 * A renovacao vive aqui, e nao em cada leitura, para que toda chamada da aplicacao
 * receba o mesmo tratamento. Uma leitura que esquecesse este passo derrubaria a
 * sessao da pessoa no meio do trabalho.
 */
async function execute(path: string, options: RequestOptions): Promise<Response> {
  const response = await send(path, options);

  // Uma sessao expirada e situacao esperada, e nao erro do usuario. A renovacao
  // acontece uma unica vez e a requisicao original e repetida.
  if (response.status === 401 && !options.skipAuth && tokens.refreshToken) {
    const renewed = await renewSession();
    if (renewed) {
      return send(path, options);
    }
    clearTokens();
    onSessionLost?.();
  }

  return response;
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json' };

  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (!options.skipAuth && tokens.accessToken) {
    headers['Authorization'] = `Bearer ${tokens.accessToken}`;
  }

  const init: RequestInit = {
    method: options.method ?? 'GET',
    headers,
  };
  if (options.body !== undefined) {
    init.body = JSON.stringify(options.body);
  }
  if (options.signal) {
    init.signal = options.signal;
  }

  try {
    return await fetch(`${baseUrl}${path}`, init);
  } catch (cause) {
    // Falha de rede nao produz resposta HTTP. Sem este tratamento, o erro
    // chegaria a interface como uma mensagem tecnica sem sentido para o usuario.
    throw new ApiError(0, {
      code: ErrorCode.NetworkUnavailable,
      message: 'Nao foi possivel falar com o servidor. Verifique a conexao.',
      details: { cause: String(cause) },
    });
  }
}

async function handle<T>(response: Response): Promise<T> {
  const envelope = await handleEnvelope<T>(response);
  return envelope.data;
}

/**
 * Devolve o envelope inteiro, com os metadados.
 *
 * Listagens paginadas precisam do total para montar a navegacao, e o total vive em
 * `meta`. Descartar o envelope em toda leitura obrigaria uma segunda requisicao
 * apenas para contar.
 */
async function handleEnvelope<T>(response: Response): Promise<Envelope<T>> {
  if (response.status === 204) {
    return { data: undefined as T };
  }

  const text = await response.text();
  const payload: unknown = text ? safeParse(text) : null;

  if (!response.ok) {
    const body = (payload as ErrorEnvelope | null)?.error;
    throw new ApiError(
      response.status,
      body ?? {
        code: ErrorCode.Internal,
        message: 'Erro inesperado no servidor.',
      },
    );
  }

  return payload as Envelope<T>;
}

/** Leitura paginada: itens e metadados na mesma resposta. */
export async function requestPage<T>(
  path: string,
  signal?: AbortSignal,
): Promise<{
  items: T[];
  total: number;
  limit: number;
  offset: number;
  // Alguns recursos acrescentam totais ao envelope de pagina, calculados no
  // servidor. Eles ficam disponiveis sem que o cliente precise de uma segunda
  // chamada para somar o que ja foi somado.
  meta: Record<string, unknown>;
}> {
  const response = await execute(path, signal ? { signal } : {});
  const envelope = await handleEnvelope<T[]>(response);
  const meta = envelope.meta ?? {};

  return {
    items: envelope.data ?? [],
    total: Number(meta.total ?? 0),
    limit: Number(meta.limit ?? 25),
    offset: Number(meta.offset ?? 0),
    meta,
  };
}

/**
 * Baixa um arquivo da API com a sessao corrente.
 *
 * O token de acesso vive em memoria e nao viaja num link comum: um `<a href>` para a
 * API chegaria sem sessao. O arquivo vem por fetch, com o cabecalho de autorizacao e
 * a mesma renovacao das outras chamadas, e so depois vira download no navegador.
 * Resposta de erro chega como ApiError, como em qualquer outra leitura.
 *
 * As opcoes servem a quem pede o arquivo por POST, como a previa do modelo, que
 * leva no corpo os valores de exemplo.
 */
export async function requestBlob(
  path: string,
  options: RequestOptions = {},
): Promise<Blob> {
  const response = await execute(path, options);
  if (!response.ok) {
    await handleEnvelope(response);
  }
  return response.blob();
}

/**
 * Renova a sessao com garantia de chamada unica.
 *
 * Varias requisicoes podem receber 401 ao mesmo tempo. Sem esta protecao, cada
 * uma dispararia uma renovacao, e como o token de renovacao roda a cada uso, a
 * segunda chamada seria tratada como reutilizacao e derrubaria a sessao.
 */
async function renewSession(): Promise<boolean> {
  refreshInFlight ??= performRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

async function performRefresh(): Promise<boolean> {
  const refreshToken = tokens.refreshToken;
  if (!refreshToken) {
    return false;
  }

  try {
    const response = await fetch(`${baseUrl}/api/v1/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!response.ok) {
      return false;
    }

    const payload = (await response.json()) as Envelope<{
      access_token: string;
      refresh_token: string;
    }>;
    setTokens(payload.data.access_token, payload.data.refresh_token);
    return true;
  } catch {
    return false;
  }
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) =>
    request<T>(path, signal ? { signal } : {}),
  post: <T>(path: string, body?: unknown, options?: { skipAuth?: boolean }) =>
    request<T>(path, {
      method: 'POST',
      ...(body !== undefined ? { body } : {}),
      ...(options?.skipAuth ? { skipAuth: true } : {}),
    }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body }),
  // PUT substitui o recurso inteiro. Ele existe ao lado do PATCH porque algumas
  // rotas editam um conjunto, e nao campos soltos: a matriz de permissoes de um
  // papel e o prazo de uma etapa sao o valor todo, e nao uma diferenca.
  put: <T>(path: string, body: unknown) => request<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

/**
 * Resolve o endereco de um arquivo do armazenamento.
 *
 * O endereco chega absoluto quando o armazenamento e um servico externo, e relativo
 * quando os arquivos ficam no disco da propria instalacao: nesse caso quem atende e
 * a API, e o endereco pertence a ela.
 *
 * Resolver contra a base da API cobre os dois casos sem ramificacao. Um endereco
 * absoluto atravessa intacto, e um relativo passa a apontar para onde a aplicacao
 * de fato conversa, que nem sempre e a origem da pagina: em desenvolvimento a
 * interface e a API atendem em portas diferentes.
 */
export function enderecoDeArquivo(url: string): string {
  const base = baseUrl || window.location.origin;
  return new URL(url, base).toString();
}
